import React, { createContext, useContext, ReactNode } from "react";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import {
  supabase,
  uploadTalentProfileFile,
  uploadApplicationFile,
  deleteFile as deleteFileFromStorage,
  UploadFileResult,
  getSignedUrl
} from "@/lib/supabase";

export type DocumentType = {
  id: number;
  name?: string;
  description?: string;
  created_at?: string;
  updated_at?: string;
};

// TODO: Consider moving the type of documents to the supabase tables.
export const DOCUMENT_TYPES: Record<string, DocumentType> = {
  CV: { id: 32 },
  GERMAN_LANGUAGE_CERTIFICATE: { id: 37 },
  WORK_PERMIT: { id: 38 },
  WORK_CONTRACT_DOCUMENT: { id: 5 },
  VISA_DOCUMENT: { id: 4 },
  DEFICIT_NOTICE_DOCUMENT: { id: 11 }
};

export const getDocumentTypeById = (id: number): DocumentType | undefined => {
  return Object.values(DOCUMENT_TYPES).find(
    (documentType) => documentType.id === id
  );
};
export type Document = {
  id: string;
  document_type: DocumentType;
  file_name: string;
  file_path: string;
  parent_id?: string;
  application_id?: string;
  created_at: string;
  updated_at: string;
};

export type DocumentsResponse = {
  data: Document[];
  success: boolean;
};

interface FileUploaderContextType {
  documentTypes: DocumentType[];
  uploadTalentProfileDocument: (
    file: File,
    documentType: DocumentType,
    userId: string,
    postUploadDocumentsTableInsert?: {
      values: Record<any, any>;
    }
  ) => Promise<UploadFileResult>;
  uploadApplicationDocument: (
    file: File,
    documentType: DocumentType,
    applicationId: string,
    postUploadDocumentsTableInsert?: {
      values: Record<any, any>;
    }
  ) => Promise<UploadFileResult>;
  deleteFile: (
    bucket: "profiles" | "applications",
    documentId: string,
    filePath: string
  ) => Promise<boolean>;
  getTalentDocument: (
    talentId: string,
    documentType: DocumentType
  ) => Promise<DocumentsResponse>;
  getApplicationDocument: (
    applicationId: string,
    documentType: DocumentType
  ) => Promise<DocumentsResponse>;
  getTalentDocuments: (talentId: string) => Promise<any[]>;
  getApplicationDocuments: (applicationId: string) => Promise<any[]>;
  handleViewFile: (
    bucket: "profiles" | "applications",
    filePath: string
  ) => Promise<void>;
}

const FileUploaderContext = createContext<FileUploaderContextType | undefined>(
  undefined
);

interface FileUploaderProviderProps {
  children: ReactNode;
}

export const FileUploaderProvider: React.FC<FileUploaderProviderProps> = ({
  children
}) => {
  const { toast } = useToast();
  const { t } = useTranslation();
  const [documentTypes, setDocumentTypes] = React.useState<DocumentType[]>([]);

  React.useEffect(() => {
    async function fetchDocumentTypes() {
      try {
        const { data, error } = await supabase
          .from("document_types")
          .select("*")
          .order("name");

        if (error) throw error;

        const fetchedTypes = data || [];
        setDocumentTypes(fetchedTypes);

        // Populate DOCUMENT_TYPES with actual names and descriptions from the database
        Object.values(DOCUMENT_TYPES).forEach((docType) => {
          const dbType = fetchedTypes.find((t: any) => t.id === docType.id);
          if (dbType) {
            docType.name = dbType.name;
            docType.description = dbType.description;
            docType.created_at = dbType.created_at;
            docType.updated_at = dbType.updated_at;
          }
        });
      } catch (error) {
        console.error("Error fetching document types:", error);
      }
    }

    fetchDocumentTypes();
  }, []);

  const uploadTalentProfileDocument = async (
    file: File,
    documentType: DocumentType,
    userId: string,
    postUploadDocumentsTableInsert?: {
      values: Record<any, any>;
    }
  ): Promise<UploadFileResult> => {
    const path = `${userId}/documents/${documentType.id}`;
    try {
      if (!userId) {
        throw new Error("User ID is required for profile file uploads");
      }
      const result = await uploadTalentProfileFile(file, path);
      if (!result.success) {
        let errorMessage: string;
        switch (result.error) {
          case "INVALID_TYPE":
            errorMessage = t("common:errors.invalid_file_type");
            break;
          case "FILE_TOO_LARGE":
            errorMessage = t("common:errors.file_too_large");
            break;
          case "UPLOAD_FAILED":
          default:
            errorMessage = t("common:errors.upload_failed");
            break;
        }
        toast({
          title: t("common:status.error"),
          description: errorMessage,
          variant: "destructive"
        });
        return result;
      }

      // If a post-upload DB update is requested, perform it atomically.
      if (postUploadDocumentsTableInsert) {
        try {
          const updateValues = {
            ...postUploadDocumentsTableInsert.values,
            document_type_id: documentType.id,
            file_path: result.path,
            file_name: result.name,
            is_digital: true,
            version_type: "original"
          };
          const { error } = await supabase
            .from("documents")
            .insert(updateValues);
          if (error) {
            // Rollback storage upload if DB update fails
            await deleteFileFromStorage("profiles", result.path);
            throw error;
          }
        } catch (e) {
          // Generic error feedback
          toast({
            title: t("common:status.error"),
            description: t("common:errors.upload_failed"),
            variant: "destructive"
          });
          return {
            success: false,
            error: "UPLOAD_FAILED"
          };
        }
      }

      // Success toast only after both storage and optional DB update succeed
      toast({
        title: t("common:success"),
        description: t("common:messages.file_uploaded_successfully")
      });

      return result;
    } catch (error) {
      console.error("File upload error:", error);
      toast({
        title: t("common:status.error"),
        description: t("common:errors.upload_failed"),
        variant: "destructive"
      });
      return {
        success: false,
        error: "UPLOAD_FAILED"
      };
    }
  };

  const uploadApplicationDocument = async (
    file: File,
    documentType: DocumentType,
    applicationId?: string,
    // Defines the values to be inserted into the documents table after a file is uploaded.
    // If uploading to a talent profile bucket for example, this should contain the talent_id.
    // Similarly, if uploading to an application bucket, this should contain the application_id.
    postUploadDocumentsTableInsert?: {
      values: Record<any, any>;
    }
  ): Promise<UploadFileResult> => {
    const path = `${applicationId}/documents/${documentType.id}`;
    try {
      if (!applicationId) {
        throw new Error(
          "Application ID is required for application file uploads"
        );
      }
      const result = await uploadApplicationFile(file, path);
      if (!result.success) {
        let errorMessage: string;
        switch (result.error) {
          case "INVALID_TYPE":
            errorMessage = t("common:errors.invalid_file_type");
            break;
          case "FILE_TOO_LARGE":
            errorMessage = t("common:errors.file_too_large");
            break;
          case "UPLOAD_FAILED":
          default:
            errorMessage = t("common:errors.upload_failed");
            break;
        }
        toast({
          title: t("common:status.error"),
          description: errorMessage,
          variant: "destructive"
        });
        return result;
      }

      // If a post-upload DB update is requested, perform it atomically.
      if (postUploadDocumentsTableInsert) {
        try {
          const updateValues = {
            ...postUploadDocumentsTableInsert.values,
            document_type_id: documentType.id,
            file_path: result.path,
            file_name: result.name,
            is_digital: true,
            version_type: "original"
          };
          const { error } = await supabase
            .from("documents")
            .insert(updateValues);
          if (error) {
            // Rollback storage upload if DB update fails
            await deleteFileFromStorage("applications", result.path);
            throw error;
          }
        } catch (e) {
          // Generic error feedback
          toast({
            title: t("common:status.error"),
            description: t("common:errors.upload_failed"),
            variant: "destructive"
          });
          return {
            success: false,
            error: "UPLOAD_FAILED"
          };
        }
      }

      // Success toast only after both storage and optional DB update succeed
      toast({
        title: t("common:success"),
        description: t("common:messages.file_uploaded_successfully")
      });

      return result;
    } catch (error) {
      console.error("File upload error:", error);
      toast({
        title: t("common:status.error"),
        description: t("common:errors.upload_failed"),
        variant: "destructive"
      });
      return {
        success: false,
        error: "UPLOAD_FAILED"
      };
    }
  };

  const deleteFile = async (
    bucket: "profiles" | "applications",
    documentId: string,
    filePath: string
  ): Promise<boolean> => {
    const result = await deleteFileFromStorage(bucket, filePath);
    if (!result.success) {
      toast({
        title: t("common:status.error"),
        description: t("common:errors.delete_failed"),
        variant: "destructive"
      });
      return false;
    }

    const { error } = await supabase
      .from("documents")
      .delete()
      .eq("id", documentId);
    if (error) {
      toast({
        title: t("common:status.error"),
        description: t("common:errors.try_again_later"),
        variant: "destructive"
      });
      return false;
    }

    toast({
      title: t("common:success"),
      description: t("common:messages.file_deleted_successfully")
    });
    return true;
  };

  const getTalentDocument = async (
    talentId: string,
    documentType: DocumentType
  ): Promise<DocumentsResponse> => {
    const { data, error } = await supabase
      .from("documents")
      .select("*")
      .eq("talent_id", talentId)
      .eq("document_type_id", documentType.id);
    if (error) {
      console.error("Error fetching talent document:", error);
      toast({
        title: t("common:status.error"),
        description: t("common:errors.try_again_later"),
        variant: "destructive"
      });
      return {
        data: [],
        success: false
      };
    }

    return {
      data: data || [],
      success: true
    };
  };

  const getApplicationDocument = async (
    applicationId: string,
    documentType: DocumentType
  ): Promise<DocumentsResponse> => {
    const { data, error } = await supabase
      .from("documents")
      .select("*")
      .eq("application_id", applicationId)
      .eq("document_type_id", documentType.id);

    if (error) {
      toast({
        title: t("common:status.error"),
        description: t("common:errors.try_again_later"),
        variant: "destructive"
      });
      return {
        data: [],
        success: false
      };
    }
    return {
      data: data || [],
      success: true
    };
  };

  const getTalentDocuments = async (talentId: string): Promise<any[]> => {
    const { data, error } = await supabase
      .from("documents")
      .select("*")
      .eq("talent_id", talentId)
      .is("application_id", null);
    if (error) {
      throw error;
    }
    return data;
  };

  const getApplicationDocuments = async (
    applicationId: string
  ): Promise<any[]> => {
    const { data, error } = await supabase
      .from("documents")
      .select("*")
      .eq("application_id", applicationId);
    if (error) {
      throw error;
    }
    return data;
  };

  const handleViewFile = async (
    bucket: "profiles" | "applications",
    filePath: string
  ): Promise<void> => {
    try {
      const signedUrl = await getSignedUrl(bucket, filePath);
      window.open(signedUrl, "_blank");
    } catch (error) {
      console.error("Error viewing file:", error);
      toast({
        title: t("common:status.error"),
        description: t("common:errors.file_view_failed"),
        variant: "destructive"
      });
    }
  };

  return (
    <FileUploaderContext.Provider
      value={{
        documentTypes,
        uploadTalentProfileDocument,
        uploadApplicationDocument,
        deleteFile,
        getTalentDocument,
        getApplicationDocument,
        getTalentDocuments,
        getApplicationDocuments,
        handleViewFile
      }}
    >
      {children}
    </FileUploaderContext.Provider>
  );
};

export const useFileUploader = (): FileUploaderContextType => {
  const context = useContext(FileUploaderContext);
  if (context === undefined) {
    throw new Error(
      "useFileUploader must be used within a FileUploaderProvider"
    );
  }
  return context;
};

// Convenience component that wraps children with the provider
export const FileUploader: React.FC<FileUploaderProviderProps> = ({
  children
}) => {
  return <FileUploaderProvider>{children}</FileUploaderProvider>;
};
